import { useEffect, useRef } from 'react';
import { MY_MOMENTS_QUERY_KEY } from '../api/moments';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { assertRecordSaved } from '../components/upload/readBack';
import { reviewsApi, CreateReviewRequest, UpdateChecklistRequest, ClientReview, SendEmailRequest, PreviewEmailRequest } from '../api/reviews';

export function useClientReviews(params?: {
  supervisorId?: string;
  bookkeeperId?: string;
  status?: string;
}) {
  return useQuery({
    queryKey: ['client-reviews', params],
    queryFn: () => reviewsApi.getReviews(params),
  });
}

/**
 * Month readiness for the review's strip. Its own request, so the review itself never waits for it, and
 * fail-soft: on any error the strip simply shows no readiness (the review is the work, the strip an aid).
 */
export function useMonthReadiness(id: string | undefined) {
  return useQuery({
    queryKey: ['month-readiness', id],
    queryFn: () => reviewsApi.getMonthReadiness(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export function useClientReview(id: string | undefined) {
  return useQuery({
    queryKey: ['client-review', id],
    queryFn: () => reviewsApi.getReview(id!),
    enabled: !!id,
  });
}

/**
 * Fires the "reviewer has this review open" signal once per mounted review, so the API can stamp
 * `startedAt` on genuine first interaction instead of at the Start-Review button press (which, in
 * practice, happens seconds before Complete).
 *
 * Deliberately a `useEffect` + POST rather than a side effect of `useClientReview`: the reviews grid
 * calls `prefetchClientReview` on row hover, which shares `useClientReview`'s query function, so any
 * stamp hung off the detail *fetch* would fire for every row the mouse crossed. The one-shot ref
 * also keeps a re-render, a SignalR-driven refetch or a query invalidation from re-signalling.
 *
 * Best-effort and non-blocking: this is measurement, never a gate on the reviewer's work, so a
 * failure is swallowed and the page renders exactly as before.
 */
export function useMarkReviewOpened(id: string | undefined): void {
  const signalledFor = useRef<string | null>(null);

  useEffect(() => {
    if (!id || signalledFor.current === id) return;
    signalledFor.current = id;
    void reviewsApi.markReviewOpened(id).catch(() => {
      /* measurement must never break the page */
    });
  }, [id]);
}

export function useCreateReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateReviewRequest) => reviewsApi.createReview(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-reviews'] });
    },
  });
}

/**
 * Change one review's reviewer, through the SAME audited, server-canonicalized path as the grid's bulk reassign
 * (POST client-reviews-bulk, one id). The server stores the person's systemuserid and their staff-profile name, so
 * this can never create a second identity for someone (Christian, 2026-10-27: Elvia appeared twice). It replaced
 * a free-text name sent to the PATCH endpoint, which ignored the field: the dialog saved nothing.
 */
export function useReassignReviewer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, supervisorId, supervisorName }: { id: string; supervisorId: string; supervisorName: string }) => {
      const result = await reviewsApi.bulk({ action: 'reassign-reviewer', ids: [id], supervisorId, supervisorName });
      const row = result.results.find((r) => r.id === id);
      if (!row || (row.outcome !== 'done' && row.outcome !== 'skipped')) {
        throw new Error(row?.reason ?? 'The reviewer could not be changed.');
      }
      return row;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['client-review', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['client-reviews'] });
    },
  });
}

export function useUpdateReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ClientReview> }) =>
      reviewsApi.updateReview(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['client-review', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['client-reviews'] });
    },
  });
}

export function useUpdateChecklist() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: UpdateChecklistRequest }) =>
      reviewsApi.updateChecklist(id, request),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['client-review', variables.id] });
    },
  });
}

export function useStartReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => reviewsApi.startReview(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['client-review', id] });
      queryClient.invalidateQueries({ queryKey: ['client-reviews'] });
    },
  });
}

export function useCompleteReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => reviewsApi.completeReview(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['client-review', id] });
      queryClient.invalidateQueries({ queryKey: ['client-reviews'] });
      // A completion may be a new accomplishment moment; the server decides.
      queryClient.invalidateQueries({ queryKey: MY_MOMENTS_QUERY_KEY });
    },
  });
}

/**
 * What saving a period would do (spec §10.13): computed by the server with the same rule the save runs, so
 * the dialog shows exactly what is saved. Pass `null` bounds while the inputs are not a valid range.
 */
export function usePeriodPreview(id: string, periodStart: string | null, periodEnd: string | null) {
  return useQuery({
    queryKey: ['period-preview', id, periodStart, periodEnd],
    queryFn: () => reviewsApi.previewPeriod(id, periodStart!, periodEnd!),
    enabled: !!periodStart && !!periodEnd,
    staleTime: 30 * 1000,
    retry: false,
  });
}

export function useUpdateReviewPeriod() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: { periodStart: string; periodEnd: string; reason?: string } }) =>
      reviewsApi.updatePeriod(id, body),
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['client-review', variables.id] });
      // The reviews this edit moved changed too (§10.13.2).
      for (const a of result?.adjusted ?? []) queryClient.invalidateQueries({ queryKey: ['client-review', a.reviewId] });
      queryClient.invalidateQueries({ queryKey: ['client-reviews'] });
      queryClient.invalidateQueries({ queryKey: ['period-preview', variables.id] });
    },
  });
}

export function useCancelReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: { closeReason: string; note?: string } }) =>
      reviewsApi.cancelReview(id, body),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['client-review', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['client-reviews'] });
    },
  });
}

export function useReinstateReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: { reason: string } }) => reviewsApi.reinstateReview(id, body),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['client-review', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['client-reviews'] });
    },
  });
}

export function useMergeReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: { withReviewId: string; reason?: string } }) =>
      reviewsApi.mergeReview(id, body),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['client-review', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['client-review', variables.body.withReviewId] });
      queryClient.invalidateQueries({ queryKey: ['client-reviews'] });
    },
  });
}

export function useDeleteReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => reviewsApi.deleteReview(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-reviews'] });
    },
  });
}

export function useAvailableClientsForReview() {
  return useQuery({
    queryKey: ['available-clients-for-review'],
    queryFn: () => reviewsApi.getAvailableClients(),
  });
}

export function useSuggestedReviews() {
  return useQuery({
    queryKey: ['suggested-reviews'],
    queryFn: () => reviewsApi.getSuggestedReviews(),
  });
}

export function useSendReviewEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: SendEmailRequest }) =>
      reviewsApi.sendEmail(id, request),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['client-review', variables.id] });
    },
  });
}

export function usePreviewReviewEmail() {
  return useMutation({
    mutationFn: ({ id, request }: { id: string; request?: PreviewEmailRequest }) =>
      reviewsApi.previewEmail(id, request),
  });
}

export function useUploadDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      reviewId,
      file,
      documentType,
      uploadedBy,
    }: {
      reviewId: string;
      file: File;
      documentType: string;
      uploadedBy?: string;
    }) => {
      const record = await reviewsApi.uploadDocument(reviewId, file, documentType, uploadedBy);
      // READ BACK (docs/specs/review-document-integrity.md S2): the file counts as uploaded only if its
      // record is on the review as re-read now. A 200 is not that evidence: Elvia's Balance Sheet got a
      // 200 and a record id while a concurrent write dropped the record (2026-10-21).
      const saved = await reviewsApi.getReview(reviewId);
      assertRecordSaved(saved, record?.id, file.name);
      return { record, saved };
    },
    onSuccess: ({ saved }, variables) => {
      // The re-read IS the fresh review (the detail query's own queryFn), so it goes straight in.
      queryClient.setQueryData(['client-review', variables.reviewId], saved);
    },
    onError: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['client-review', variables.reviewId] });
    },
  });
}

export function useDeleteDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ reviewId, documentId }: { reviewId: string; documentId: string }) =>
      reviewsApi.deleteDocument(reviewId, documentId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['client-review', variables.reviewId] });
    },
  });
}

/**
 * Background/intent prefetch of the reviews list — key matches useClientReviews() with no params.
 * Staleness: the shared queryClient's default staleTime (5 min) governs — prefetching fresh data is a no-op.
 */
export function prefetchClientReviews(qc: QueryClient): Promise<void> {
  return qc.prefetchQuery({ queryKey: ['client-reviews', undefined], queryFn: () => reviewsApi.getReviews(undefined) });
}

/**
 * Row-hover prefetch of one review's detail — key matches useClientReview(id).
 * Staleness: the shared queryClient's default staleTime (5 min) governs — prefetching fresh data is a no-op.
 */
export function prefetchClientReview(qc: QueryClient, id: string): Promise<void> {
  return qc.prefetchQuery({ queryKey: ['client-review', id], queryFn: () => reviewsApi.getReview(id) });
}
